<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { AccountFormContent } from '@client/pages/marketing/targeting/accounts/modals/shared';
  import { GroupFormContent } from '@client/pages/marketing/targeting/groups/modals/shared';

  import { CampaignsService } from '@client/services/campaigns';
  import { GroupsService } from '@client/services/groups';
  import { TagsService } from '@client/services/tags';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';

  import { generatePastelColors } from '@common/utils/colors';

  import { TargetingList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const targetingTypes = [
    { label: $format('label.GROUP'), value: 'group' },
    { label: $format('label.DOMAIN'), value: 'domain' },
    { label: $format('label.RECIPIENT'), value: 'recipient' }
  ];

  const groupTypes = [
    { label: $format('label.RECIPIENT'), value: 'recip' },
    { label: $format('label.SENDER'), value: 'sender' }
  ];

  let loading = true;
  let loadingType = false;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let selectedType = targetingTypes[0];
  let selectedTargetId = null;
  let targets = [];
  let targetData = null;
  let originalTargetData = null;
  let isDisabled = false;

  let campaigns = [];
  let tags = [];
  let groups = [];
  let allTargeting = {};

  const colors = generatePastelColors(1000);
  const colorAssignments = {};

  const createDatasetOptions = (value) => {
    if (!colorAssignments[value]) {
      colorAssignments[value] = colors.pop();
    }

    return {
      color: colorAssignments[value],
      label: value,
      value: value
    };
  };

  const getCampaigns = async () => {
    const response = await CampaignsService.getAll();
    campaigns = Array.isArray(response) ? response.map(({ rulename }) => createDatasetOptions(rulename)) : [];
  };

  const getGroups = async () => {
    const response = await GroupsService.getSenderGroups();
    groups = Array.isArray(response) ? response.map(({ groupname }) => createDatasetOptions(groupname)) : [];
  };

  const getTags = async () => {
    const response = await TagsService.getAll();
    tags = Array.isArray(response) ? response.map((text) => createDatasetOptions(text)) : [];
  };

  const loadTargeting = async () => {
    const targeting = await TargetingService.get();
    allTargeting = targeting || { accounts: [], departments: [] };

    if (selectedType.value === 'group') {
      targets = (allTargeting.departments || []).map((dept, index) => ({
        ...dept,
        _id: dept._id || `dept_${index}`,
        priority: index + 1
      }));
    } else {
      const accounts = allTargeting.accounts || [];
      if (selectedType.value === 'domain') {
        targets = accounts.filter((acc) => acc.name.includes('@') === false);
      } else {
        targets = accounts.filter((acc) => acc.name.includes('@') === true);
      }
    }
  };

  const selectTarget = (targetId) => {
    selectedTargetId = targetId;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0) {
      if (selectedType.value === 'group') {
        const selected = targets.find((t) => t._id === selectedTargetId);
        if (selected) {
          originalTargetData = { ...selected };
          targetData = {
            ...selected,
            groups: selected.sendergroups?.map((g) => ({ label: g.groupname, value: g.groupname })) || [],
            campaigns: selected.ads?.map((a) => ({ label: a.text, value: a.text })) || [],
            tags: selected.tags?.map((t) => ({ label: t.text, value: t.text })) || [],
            target: selected.targettype,
            status: !!selected.status
          };
        }
      } else {
        const selected = targets.find((t) => t.name === selectedTargetId);
        if (selected) {
          originalTargetData = { ...selected };
          targetData = {
            ...selected,
            campaigns: selected.ads?.map((a) => ({ label: a.text, value: a.text })) || [],
            tags: selected.tags?.map((t) => ({ label: t.text, value: t.text })) || []
          };
        }
      }

      currentInternalStep = 1;
      if (currentInternalStep > maxStepReached) {
        maxStepReached = currentInternalStep;
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const handleTypeChange = async () => {
    loadingType = true;
    selectedTargetId = null;
    targetData = null;
    originalTargetData = null;
    currentInternalStep = 0;
    maxStepReached = 0;
    await loadTargeting();
    loadingType = false;
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      // Fetch fresh targeting to avoid overwriting concurrent changes
      const currentTargeting = await TargetingService.get();
      let updatedTargeting;

      if (selectedType.value === 'group') {
        const updatedDepartment = {
          targettype: typeof targetData.target === 'string' ? targetData.target : targetData.target.value,
          sendergroups: (targetData.groups || []).map(({ value }) => ({ groupname: value })),
          ads: (targetData.campaigns || []).map(({ value }) => ({ text: value })),
          tags: (targetData.tags || []).map(({ value }) => ({ text: value }))
        };

        if (targetData.status) {
          updatedDepartment.status = targetData.status;
        }

        const departments = currentTargeting.departments.map((dept) => {
          if (dept._id === targetData._id) return updatedDepartment;
          const { _id, ...rest } = dept;
          return rest;
        });

        updatedTargeting = {
          accounts: currentTargeting.accounts || [],
          departments
        };
      } else {
        const updatedAccount = {
          name: targetData.name,
          tags: (targetData.tags || []).map(({ value }) => ({ text: value })),
          ads: (targetData.campaigns || []).map(({ value }) => ({ text: value }))
        };

        const accounts = currentTargeting.accounts.map((acc) =>
          acc.name === originalTargetData.name ? updatedAccount : acc
        );

        updatedTargeting = {
          accounts,
          departments: currentTargeting.departments || []
        };
      }

      await TargetingService.update(updatedTargeting);

      notify($format('label.SUCCESS.TARGET_UPDATED'));

      step.data = {
        id: selectedType?.value === 'group' ? 'Groups' : selectedType?.value === 'domain' ? 'Domains' : 'Recipients',
        rollbackData: {
          originalTargetData,
          allTargeting: currentTargeting
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_TARGET'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      const { allTargeting } = step.data.rollbackData;

      if (!allTargeting) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      await TargetingService.update(allTargeting);

      notify($format('label.SUCCESS.ROLLBACK_TARGET'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_TARGET'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      await Promise.all([getCampaigns(), getGroups(), getTags(), loadTargeting()]);
    } catch (error) {
      console.error('Failed to load targeting data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      isDisabled = !selectedTargetId || loading || loadingType;
    } else if (currentInternalStep === 1) {
      if (selectedType.value === 'group') {
        isDisabled = !targetData?.groups?.length || loading || loadingType;
      } else {
        isDisabled = !targetData?.name || loading || loadingType;
      }
    }
  }

  $: nextDisabled = currentInternalStep !== 1 || isDisabled;

  $: selectedTargetSummary = selectedTargetId
    ? selectedType.value === 'group'
      ? $format('label.GROUP_TARGET_SELECTED')
      : targets.find((t) => t.name === selectedTargetId)?.name || $format('label.TARGET_SELECTED')
    : $format('label.NOT_SELECTED');
</script>

<div class="targeting-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_TARGET_TYPE_AND_TARGET')}
    summary={selectedTargetSummary}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={isDisabled}
  >
    <div class="type-selector">
      <p>{$format('label.TARGET_TYPE')}</p>
      <Select
        items={targetingTypes}
        bind:value={selectedType}
        clearable={false}
        showChevron
        on:change={handleTypeChange}
      />
    </div>

    <div class="target-list">
      <TargetingList
        {targets}
        bind:selectedTargetId
        onSelectTarget={selectTarget}
        targetType={selectedType.value}
        loading={loadingType || loading}
      />
    </div>
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_TARGET')}
    summary={targetData?.name || targetData?.groups?.[0]?.value || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={isDisabled}
  >
    {#if targetData}
      {#if selectedType.value === 'group'}
        <GroupFormContent bind:route={targetData} types={groupTypes} {groups} {campaigns} {tags} />
      {:else}
        <AccountFormContent
          bind:data={targetData}
          isDomains={selectedType.value === 'domain'}
          {campaigns}
          {tags}
          valid={true}
          onKeyUpHandler={() => {}}
        />
      {/if}
    {/if}
  </Step>
</div>

<style lang="scss">
  .targeting-update-form {
    .type-selector {
      margin-bottom: var(--margin-large);

      p {
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }

    .target-list {
      margin-top: var(--margin-large);
    }

    :global {
      .target {
        margin-bottom: var(--margin-base);
      }
    }
  }
</style>
